import { useState, useEffect, useCallback } from 'react';
import { Cpu, FileText, Save, RefreshCw, CheckCircle, AlertTriangle, Loader2, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const TABS = [
  { id: 'models',  label: 'Models',  icon: Cpu },
  { id: 'prompts', label: 'Prompts', icon: FileText },
  { id: 'image',   label: 'Image',   icon: ImageIcon },
];

const cardCls  = 'bg-card border border-border rounded-xl';
const labelCls = 'block text-sm font-medium text-foreground mb-1.5';

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

function HealthBadge({ health, checking }) {
  if (checking) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-[#6E6258]">
        <Loader2 size={13} className="animate-spin" />
        Checking endpoint...
      </span>
    );
  }
  if (!health) return null;
  if (health.ok) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600">
        <CheckCircle size={13} />
        Endpoint reachable — {health.available_models?.length || 0} models loaded
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-amber-600">
      <AlertTriangle size={13} />
      {health.error || 'Endpoint unreachable'}
    </span>
  );
}

export default function AIInferencePage() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin';

  const [activeTab, setActiveTab] = useState('models');
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [checking,  setChecking]  = useState(false);

  const [models,    setModels]    = useState([]);
  const [providers, setProviders] = useState({ ollama: false, openai: false });
  const [health,    setHealth]    = useState(null);
  const [endpoint,  setEndpoint]  = useState(null);
  const [defaultMissing, setDefaultMissing] = useState(false);

  const [form, setForm] = useState({
    default_model_id:      '',
    system_prompt:         '',
    house_style:           '',
    image_prompt_template: '',
  });
  const [original, setOriginal] = useState(null);
  const [promptSource, setPromptSource] = useState('code');
  const [imagePromptSource, setImagePromptSource] = useState('code');
  const [placeholders, setPlaceholders] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [modelsRes, settingsRes] = await Promise.all([
        api.get('/ai/models'),
        api.get('/ai/settings'),
      ]);
      setModels(modelsRes.data.models || []);
      setProviders(modelsRes.data.providers || { ollama: false, openai: false });
      setEndpoint(modelsRes.data.endpoint || null);
      setDefaultMissing(!!modelsRes.data.default_missing);
      const next = {
        default_model_id:      settingsRes.data.default_model_id      || modelsRes.data.default_id || '',
        system_prompt:         settingsRes.data.system_prompt         || '',
        house_style:           settingsRes.data.house_style           || '',
        image_prompt_template: settingsRes.data.image_prompt_template || '',
      };
      setForm(next);
      setOriginal(next);
      setPromptSource(settingsRes.data.prompt_source || 'code');
      setImagePromptSource(settingsRes.data.image_prompt_source || 'code');
      setPlaceholders(settingsRes.data.image_placeholders || []);
      setUpdatedAt(settingsRes.data.updated_at);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkHealth = useCallback(async () => {
    setChecking(true);
    try {
      const res = await api.get('/ai/health');
      setHealth(res.data);
    } catch {
      setHealth({ ok: false, error: 'Health check failed' });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { load(); checkHealth(); }, [load, checkHealth]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/ai/settings', form);
      setUpdatedAt(res.data.updated_at);
      setPromptSource(res.data.prompt_source || 'database');
      setImagePromptSource(res.data.image_prompt_source || 'database');
      const next = {
        default_model_id:      res.data.default_model_id      || form.default_model_id,
        system_prompt:         res.data.system_prompt         || '',
        house_style:           res.data.house_style           || '',
        image_prompt_template: res.data.image_prompt_template || '',
      };
      setForm(next);
      setOriginal(next);
      toast.success('AI settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const selected = models.find(m => m.id === form.default_model_id);
  const dirty    = original && JSON.stringify(form) !== JSON.stringify(original);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6E6258] py-12">
        <Loader2 size={16} className="animate-spin" />
        Loading AI settings...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-inference-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1F1A17] font-outfit tracking-tight">AI Inference</h1>
        <p className="text-sm text-[#6E6258] mt-1">
          Model selection and editorial prompt configuration for story generation.
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === t.id
                  ? 'border-[#1F1A17] text-[#1F1A17]'
                  : 'border-transparent text-[#9C8E84] hover:text-[#6E6258]'
              }`}
              data-testid={`tab-${t.id}`}
            >
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'models' && (
        <div className={`${cardCls} p-6 space-y-5`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-foreground font-outfit">Default Model</h2>
              <p className="text-xs text-[#9C8E84] mt-0.5">
                Used for all story generation in Tempust. Managed in Okebourne's AI Inference tab.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={checkHealth}
              disabled={checking}
              className="shrink-0"
            >
              <RefreshCw size={14} className={`mr-2 ${checking ? 'animate-spin' : ''}`} />
              Test Connection
            </Button>
          </div>

          <HealthBadge health={health} checking={checking} />

          {defaultMissing && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                The saved model is no longer in the registry, so generation will fail.
                Pick one from the list below and save.
              </span>
            </div>
          )}

          <div>
            <label className={labelCls}>Model</label>
            <select
              value={form.default_model_id}
              onChange={e => update('default_model_id', e.target.value)}
              disabled={!isAdmin}
              className="w-full bg-white text-[#1F1A17] border border-border rounded-md px-3 py-2 text-sm disabled:opacity-60"
              data-testid="model-select"
            >
              {defaultMissing && (
                <option value={form.default_model_id}>
                  {form.default_model_id} — no longer available
                </option>
              )}
              {models.map(m => (
                <option key={m.id} value={m.id} disabled={!m.available}>
                  {m.label}
                  {!m.available ? ' — not configured' : ''}
                  {m.available && m.installed === false ? ' — not on the GPU' : ''}
                </option>
              ))}
            </select>
            {selected && (
              <p className="text-xs text-[#9C8E84] mt-1.5">{selected.description}</p>
            )}
          </div>

          <div className="bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg px-4 py-3 space-y-1">
            <p className="text-xs text-[#6E6258]">
              Self-hosted endpoint:{' '}
              <span className={providers.ollama ? 'text-emerald-600' : 'text-amber-600'}>
                {providers.ollama ? 'configured' : 'not configured'}
              </span>
              {endpoint?.host && (
                <span className="text-[#9C8E84]"> — {endpoint.host}</span>
              )}
            </p>
            <p className="text-xs text-[#6E6258]">
              Azure OpenAI:{' '}
              <span className={providers.openai ? 'text-emerald-600' : 'text-amber-600'}>
                {providers.openai ? 'configured' : 'not configured — add credentials to re-enable'}
              </span>
            </p>
          </div>

          {health?.ok && health.available_models?.length > 0 && (
            <div>
              <label className={labelCls}>Models present on the endpoint</label>
              <div className="flex flex-wrap gap-1.5">
                {health.available_models.map(m => (
                  <span
                    key={m}
                    className="text-xs px-2 py-0.5 rounded-full bg-[#FFF4E8] border border-[#E7D2B8] text-[#6E6258]"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'prompts' && (
        <div className={`${cardCls} p-6 space-y-5`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-foreground font-outfit">Editorial Prompt</h2>
              <p className="text-xs text-[#9C8E84] mt-0.5">
                {promptSource === 'database'
                  ? 'This saved prompt is used for every story generated.'
                  : 'Showing the built-in prompt. Edit and save to take over from it.'}
              </p>
            </div>
            {dirty && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setForm({ ...original })}
                className="shrink-0"
              >
                <RotateCcw size={14} className="mr-2" />
                Discard changes
              </Button>
            )}
          </div>

          <div>
            <label className={labelCls}>
              System Prompt
              <span className="text-[#9C8E84] font-normal ml-1">— the full draft instructions</span>
            </label>
            <div className="flex items-center gap-3 text-xs text-[#9C8E84] mb-1.5">
              <span>{form.system_prompt.length.toLocaleString()} characters</span>
              {dirty && <span className="text-amber-600 font-medium">Unsaved changes</span>}
            </div>
            <Textarea
              value={form.system_prompt}
              onChange={e => update('system_prompt', e.target.value)}
              disabled={!isAdmin}
              className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[360px] font-mono text-xs"
              data-testid="system-prompt"
            />
          </div>

          <div>
            <label className={labelCls}>
              House Style
              <span className="text-[#9C8E84] font-normal ml-1">— appended to every generation</span>
            </label>
            <Textarea
              value={form.house_style}
              onChange={e => update('house_style', e.target.value)}
              disabled={!isAdmin}
              placeholder="Optional"
              className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[120px]"
              data-testid="house-style"
            />
          </div>
        </div>
      )}

      {activeTab === 'image' && (
        <div className={`${cardCls} p-6 space-y-5`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-foreground font-outfit">Image Prompt</h2>
              <p className="text-xs text-[#9C8E84] mt-0.5">
                {imagePromptSource === 'database'
                  ? 'This saved template is used for every AI image generated.'
                  : 'Showing the built-in template. Edit and save to take over from it.'}
              </p>
            </div>
            {dirty && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setForm({ ...original })}
                className="shrink-0"
              >
                <RotateCcw size={14} className="mr-2" />
                Discard changes
              </Button>
            )}
          </div>

          {placeholders.length > 0 && (
            <div className="bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg px-4 py-3 space-y-2">
              <p className="text-xs text-[#6E6258]">
                Placeholders below are replaced with the actual story content.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map(p => (
                  <span
                    key={p}
                    className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#E7D2B8] text-[#6E6258] font-mono"
                  >
                    {`{{${p}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>
              Prompt Template
              <span className="text-[#9C8E84] font-normal ml-1">— sent to the image model</span>
            </label>
            <div className="flex items-center gap-3 text-xs text-[#9C8E84] mb-1.5">
              {dirty && <span className="text-amber-600 font-medium">Unsaved changes</span>}
            </div>
            <Textarea
              value={form.image_prompt_template}
              onChange={e => update('image_prompt_template', e.target.value)}
              disabled={!isAdmin}
              className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] min-h-[420px] font-mono text-xs"
              data-testid="image-prompt-template"
            />
            <p className="text-xs text-[#9C8E84] mt-1.5">
              Keep the finished prompt under roughly 600 words. Longer prompts cause the model to
              drop instructions, and the rules at the end are the first to go.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={handleSave}
          disabled={saving || !isAdmin}
          className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4] gap-2"
          data-testid="save-ai-settings"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Settings
        </Button>
        {!isAdmin && (
          <span className="text-xs text-[#9C8E84]">Read-only — admin access required to change settings.</span>
        )}
        {updatedAt && (
          <span className="text-xs text-[#9C8E84]">Last updated {fmtUK(updatedAt)}</span>
        )}
      </div>
    </div>
  );
}